/**
 * API layer for the app-based pricing admin.
 *
 * Calls the real backend endpoints under /app-pricing/.
 */

import { api } from './api';
import type { AppId, Tier, AddOnId } from '../pricing/data';

const API_URL = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Types — backend response shapes
// ---------------------------------------------------------------------------

export interface ApiApp {
  id: AppId;
  name: string;
  description: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAppPlan {
  id: string;
  appId: AppId;
  tier: Tier;
  active: boolean;
  prices: Record<string, number>;
  features: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAddOn {
  id: string;
  name: string;
  description: string | null;
  stackable: boolean;
  unit: string | null;
  /** Max quantity per org; null = uncapped. Enforced server-side
   * on /subscribe and /charge-preview. */
  maxQuantity: number | null;
  active: boolean;
  prices: Record<string, number>;
  createdAt: string;
  updatedAt: string;
}

export interface ApiBundleDiscount {
  id: number;
  minApps: number;
  discountPercent: number;
}

export interface OrgAppSubscription {
  id: number;
  orgId: number;
  appId: AppId;
  appPlanId: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** When the (org, app) sub first consumed its trial; null means never trialed. */
  trialUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Line item on a prorated charge preview. */
export interface ProratedAppLine {
  appId: string;
  tier: string;
  appPlanId: string;
  fullMonthly: number;
  due: number;
  kind: 'new' | 'renewal' | 'upgrade' | 'paid' | 'downgrade';
}

export interface ProratedAddOnLine {
  addOnId: string;
  quantity: number;
  lastPaidQuantity: number;
  unitPrice: number;
  due: number;
  kind: 'new' | 'renewal' | 'increase' | 'paid' | 'decrease';
}

/**
 * Response shape of GET /app-pricing/orgs/:orgId/charge — the
 * prorated "what they owe right now" complement to /invoice (which
 * returns the full recurring monthly).
 */
export interface ProratedCharge {
  currency: string;
  /** ISO timestamp of the next billing event after this payment. */
  nextAnchor: string;
  /** ISO timestamp of the anchor BEFORE this payment (null on first
   * cycle). */
  currentAnchor: string | null;
  /** True = full-cycle charge (renewal or first payment). False =
   * prorated mid-cycle addition. */
  isFullCycle: boolean;
  daysUntilAnchor: number;
  apps: ProratedAppLine[];
  addOns: ProratedAddOnLine[];
  appSubtotal: number;
  addOnSubtotal: number;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
}

/** Response shape of POST /app-pricing/orgs/:orgId/pay. */
export interface InitiatePaymentResponse {
  id: string;
  link: string;
  paymentReference: string;
  invoice: {
    currency: string;
    total: number;
    discountPercent: number;
    apps: Array<{ appId: string; tier: string; price: number }>;
    addOns: Array<{
      addOnId: string;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
    }>;
  };
}

export interface OrgAddOn {
  id: number;
  orgId: number;
  addOnId: string;
  quantity: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Admin org type
// ---------------------------------------------------------------------------

export interface AdminOrg {
  id: number;
  name: string;
  createdAt: string;
  subscriptions: Array<{
    appId: AppId;
    appPlanId: string;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  }>;
  addOns: Array<{
    addOnId: string;
    quantity: number;
  }>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const appPricingApi = {
  /** Returns the list of active apps. */
  getApps: (): Promise<ApiApp[]> =>
    api.get(`${API_URL}/app-pricing/apps`, 'Failed to fetch apps'),

  /** Returns all plans with features/prices. */
  getAppPlans: (): Promise<ApiAppPlan[]> =>
    api.get(`${API_URL}/app-pricing/plans`, 'Failed to fetch app plans'),

  /** Returns all add-ons with prices. */
  getAddOns: (): Promise<ApiAddOn[]> =>
    api.get(`${API_URL}/app-pricing/add-ons`, 'Failed to fetch add-ons'),

  /** Returns the bundle discount tiers. */
  getBundleDiscounts: (): Promise<ApiBundleDiscount[]> =>
    api.get(
      `${API_URL}/app-pricing/bundle-discounts`,
      'Failed to fetch bundle discounts'
    ),

  /** Returns all orgs with subscription summaries (superadmin view). */
  getOrgs: (): Promise<AdminOrg[]> =>
    api.get(
      `${API_URL}/app-pricing/admin/orgs`,
      'Failed to fetch organizations'
    ),

  /** Returns an org's app subscriptions. */
  getOrgAppSubscriptions: (orgId: number): Promise<OrgAppSubscription[]> =>
    api.get(
      `${API_URL}/app-pricing/orgs/${orgId}/subscriptions`,
      'Failed to fetch org subscriptions'
    ),

  /** Returns an org's add-ons. */
  getOrgAddOns: (orgId: number): Promise<OrgAddOn[]> =>
    api.get(
      `${API_URL}/app-pricing/orgs/${orgId}/add-ons`,
      'Failed to fetch org add-ons'
    ),

  /**
   * Subscribe an org to apps + add-ons (batch).
   * POST /app-pricing/orgs/:orgId/subscribe
   */
  subscribeOrg: (
    orgId: number,
    apps: Array<{ appId: AppId; tier: Tier }>,
    addOns?: Array<{ addOnId: string; quantity: number }>
  ): Promise<{
    subscriptions: OrgAppSubscription[];
    addOns: OrgAddOn[];
    limits: unknown;
  }> =>
    api.post(
      `${API_URL}/app-pricing/orgs/${orgId}/subscribe`,
      { apps, addOns },
      'Failed to update org subscription'
    ),

  /**
   * Superadmin: replace any org's apps + add-ons without being a
   * member of that org. POST /app-pricing/admin/orgs/:orgId/subscribe
   */
  adminSubscribeOrg: (
    orgId: number,
    apps: Array<{ appId: AppId; tier: Tier }>,
    addOns?: Array<{ addOnId: string; quantity: number }>,
    cancelAppIds?: AppId[]
  ): Promise<{
    subscriptions: OrgAppSubscription[];
    addOns: OrgAddOn[];
    limits: unknown;
  }> =>
    api.post(
      `${API_URL}/app-pricing/admin/orgs/${orgId}/subscribe`,
      { apps, addOns, cancelAppIds },
      'Failed to update org subscription'
    ),

  /** Superadmin: subscription audit trail for an org. */
  getOrgSubscriptionEvents: (
    orgId: number,
    opts?: { appId?: string; limit?: number; offset?: number }
  ): Promise<
    Array<{
      id: number;
      orgId: number;
      appId: string | null;
      appPlanId: string | null;
      eventType: string;
      metadata: Record<string, unknown>;
      actorEmail: string | null;
      createdAt: string;
    }>
  > => {
    const params = new URLSearchParams();
    if (opts?.appId) params.set('appId', opts.appId);
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    if (opts?.offset != null) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return api.get(
      `${API_URL}/app-pricing/admin/orgs/${orgId}/events${qs ? `?${qs}` : ''}`,
      'Failed to fetch subscription events'
    );
  },

  /**
   * Update an org's add-on quantity.
   * PUT /app-pricing/orgs/:orgId/add-ons/:addOnId
   */
  updateOrgAddOn: (
    orgId: number,
    addOnId: string,
    quantity: number
  ): Promise<OrgAddOn | { removed: boolean }> =>
    api.put(
      `${API_URL}/app-pricing/orgs/${orgId}/add-ons/${addOnId}`,
      { quantity },
      'Failed to update add-on'
    ),

  /**
   * Update prices for an app plan across currencies.
   * PUT /app-pricing/plans/:planId/prices
   */
  updatePlanPrices: (
    planId: string,
    prices: Record<string, number>
  ): Promise<ApiAppPlan> =>
    api.put(
      `${API_URL}/app-pricing/plans/${planId}/prices`,
      { prices },
      'Failed to update prices'
    ),

  /**
   * Update prices for an add-on across currencies.
   * PUT /app-pricing/add-ons/:addOnId/prices
   */
  updateAddOnPrices: (
    addOnId: string,
    prices: Record<string, number>
  ): Promise<ApiAddOn> =>
    api.put(
      `${API_URL}/app-pricing/add-ons/${addOnId}/prices`,
      { prices },
      'Failed to update prices'
    ),

  /**
   * Update features for an app plan.
   * PUT /app-pricing/plans/:planId/features
   */
  updatePlanFeatures: (
    planId: string,
    features: Record<string, string | null>
  ): Promise<ApiAppPlan> =>
    api.put(
      `${API_URL}/app-pricing/plans/${planId}/features`,
      { features },
      'Failed to update features'
    ),

  /** Cancel a specific app subscription. */
  cancelAppSubscription: (orgId: number, appId: AppId): Promise<unknown> =>
    api.post(
      `${API_URL}/app-pricing/orgs/${orgId}/apps/${appId}/cancel`,
      {},
      'Failed to cancel subscription'
    ),

  /** Fetch allowed payment vendors for an org. */
  getPaymentVendors: (orgId: number): Promise<unknown> =>
    api.get(
      `${API_URL}/app-pricing/orgs/${orgId}/payment-vendors`,
      'Failed to fetch payment vendors'
    ),

  /** Initiate a payment flow (returns payment link). */
  initiatePayment: (
    orgId: number,
    vendor?: string,
    metadata?: Record<string, unknown>
  ): Promise<InitiatePaymentResponse> =>
    api.post(
      `${API_URL}/app-pricing/orgs/${orgId}/pay`,
      { vendor, metadata },
      'Failed to initiate payment'
    ) as Promise<InitiatePaymentResponse>,

  /** Fetch invoice breakdown for an org. */
  getInvoice: (orgId: number, currency?: string): Promise<unknown> => {
    const params = currency ? `?currency=${currency}` : '';
    return api.get(
      `${API_URL}/app-pricing/orgs/${orgId}/invoice${params}`,
      'Failed to fetch invoice'
    );
  },
};

// ---------------------------------------------------------------------------
// Public (no-auth) fetchers for landing/pricing pages
// ---------------------------------------------------------------------------

export const fetchPublicPlans = (): Promise<ApiAppPlan[]> =>
  api.get(`${API_URL}/app-pricing/plans`, 'Failed to fetch plans', true, false);

export const fetchPublicAddOns = (): Promise<ApiAddOn[]> =>
  api.get(
    `${API_URL}/app-pricing/add-ons`,
    'Failed to fetch add-ons',
    true,
    false
  );

// ---------------------------------------------------------------------------
// Convenience aliases (migrated from appCheckout.ts)
// ---------------------------------------------------------------------------

export interface AppSelection {
  appId: AppId;
  tier: Tier;
}

export interface AddOnSelection {
  addOnId: AddOnId;
  quantity: number;
}

export interface SubscribePayload {
  apps: AppSelection[];
  addOns: AddOnSelection[];
  /**
   * Explicit list of appIds to schedule for cancellation
   * (cancel_at_period_end). Opt-in rather than inferred from
   * the apps list so a stale browser tab can't accidentally
   * cancel something it doesn't know about.
   */
  cancelAppIds?: AppId[];
}

/** Subscribe to apps and add-ons (creates trialing subscriptions). */
export const subscribeToApps = async (
  orgId: number,
  payload: SubscribePayload
) => {
  return await api.post(
    `${API_URL}/app-pricing/orgs/${orgId}/subscribe`,
    payload,
    'Failed to subscribe'
  );
};

/** Fetch org's app subscriptions. */
export const getOrgSubscriptions = async (orgId: number) => {
  return await api.get(
    `${API_URL}/app-pricing/orgs/${orgId}/subscriptions`,
    'Failed to fetch subscriptions'
  );
};

/** Fetch org's active add-ons. */
export const getOrgAddOns = async (orgId: number) => {
  return await api.get(
    `${API_URL}/app-pricing/orgs/${orgId}/add-ons`,
    'Failed to fetch add-ons'
  );
};

/** Cancel a specific app subscription. */
export const cancelAppSubscription = async (orgId: number, appId: AppId) => {
  return await api.post(
    `${API_URL}/app-pricing/orgs/${orgId}/apps/${appId}/cancel`,
    {},
    'Failed to cancel subscription'
  );
};

/**
 * Undo a scheduled cancellation. Only works while the sub is
 * still active / trialing (period_end hasn't fired). Once
 * deactivated, the caller must re-subscribe via /subscribe.
 */
export const undoCancelAppSubscription = async (
  orgId: number,
  appId: AppId
) => {
  return await api.post(
    `${API_URL}/app-pricing/orgs/${orgId}/apps/${appId}/undo-cancel`,
    {},
    'Failed to undo cancellation'
  );
};

/** Fetch allowed payment vendors for an org. */
export const getPaymentVendors = async (orgId: number) => {
  return await api.get(
    `${API_URL}/app-pricing/orgs/${orgId}/payment-vendors`,
    'Failed to fetch payment vendors'
  );
};

/** Initiate a payment flow (returns payment link). */
export const initiatePayment = async (
  orgId: number,
  vendor?: string,
  metadata?: Record<string, unknown>,
  forceRenewal?: boolean
): Promise<InitiatePaymentResponse> => {
  return (await api.post(
    `${API_URL}/app-pricing/orgs/${orgId}/pay`,
    { vendor, metadata, forceRenewal },
    'Failed to initiate payment'
  )) as InitiatePaymentResponse;
};

/** Fetch invoice breakdown for an org. */
export const getInvoice = async (orgId: number, currency?: string) => {
  const params = currency ? `?currency=${currency}` : '';
  return await api.get(
    `${API_URL}/app-pricing/orgs/${orgId}/invoice${params}`,
    'Failed to fetch invoice'
  );
};

/**
 * Fetch the prorated "due today" charge plus the org's billing
 * anchor. Reads current DB state; mirrors what /pay would charge
 * if invoked right now.
 */
export const getProratedCharge = async (
  orgId: number,
  currency?: string
): Promise<ProratedCharge> => {
  const params = currency ? `?currency=${currency}` : '';
  return (await api.get(
    `${API_URL}/app-pricing/orgs/${orgId}/charge${params}`,
    'Failed to fetch prorated charge'
  )) as ProratedCharge;
};

/**
 * What-if charge preview: tell the server what the user is ABOUT
 * to submit via /subscribe and get back what the /pay that would
 * follow would charge. Used by the checkout UI to show live
 * "due today" totals as selections change.
 */
export interface ChargePreviewIntent {
  apps?: Array<{ appId: string; tier: string }>;
  addOns?: Array<{ addOnId: string; quantity: number }>;
}

export const getChargePreview = async (
  orgId: number,
  intent: ChargePreviewIntent,
  currency?: string
): Promise<ProratedCharge> => {
  const params = currency ? `?currency=${currency}` : '';
  return (await api.post(
    `${API_URL}/app-pricing/orgs/${orgId}/charge-preview${params}`,
    intent,
    'Failed to fetch charge preview'
  )) as ProratedCharge;
};
